/**
 * Every server call behind the account-restore surface, in one place.
 *
 * These were hand-rolled `fetch` calls against paths this console guessed at,
 * because the routes did not exist when the screen was built. They exist now -
 * `GET /api/v1/internal/deletion-requests`, `POST .../{request_id}/restore` and
 * `GET /api/v1/internal/users/{member_id}/deletion-request` - with the shapes
 * the screen already assumed, so everything below goes through the generated
 * client. Keeping the calls in this one module is what made the swap a change
 * of call rather than of call site: no component fetches anything itself.
 *
 * `restorability` and `remainingGrace` stay here for the same reason - whether
 * a grace window is still open is one rule, testable on its own.
 */

import {
  deletionRequestsApiV1InternalDeletionRequestsGet,
  memberDeletionRequestApiV1InternalUsersMemberIdDeletionRequestGet,
  restoreDeletionRequestApiV1InternalDeletionRequestsRequestIdRestorePost,
  type DeletionRequestItem,
  type DeletionRequestLookup,
  type DeletionRequestPage,
} from '@closet-theory/api';
import { configureAdminApi, handleUnauthorized } from './auth';

/** One row of `deletion_requests` - NOT `account_deletion_requests`, which is empty. */
export type DeletionRequest = DeletionRequestItem;
export type { DeletionRequestPage };

export type ServiceRead<T> = {
  data?: T | undefined;
  error?: unknown;
  response: { status: number };
};

/** The generated client returns this shape already; `useInsight` reads it unchanged. */
function settle<T>(result: {
  data?: T | undefined;
  error?: unknown;
  response: Response;
}): ServiceRead<T> {
  handleUnauthorized(result.response.status);
  return {
    ...(result.data === undefined ? {} : { data: result.data }),
    ...(result.error === undefined ? {} : { error: result.error }),
    response: { status: result.response.status },
  };
}

/**
 * The open deletion request for one member, if any. A member who has never
 * deleted their account answers `{ request: null }` - an absence, not a failure.
 */
export async function readMemberDeletionRequest(
  memberId: string,
): Promise<ServiceRead<DeletionRequestLookup>> {
  configureAdminApi();
  return settle(
    await memberDeletionRequestApiV1InternalUsersMemberIdDeletionRequestGet({
      path: { member_id: memberId },
    }),
  );
}

/**
 * Every deletion request, newest first. `status` is a filter, never a default:
 * the screen asks for everything unless an operator narrows it.
 */
export async function listDeletionRequests(query: {
  limit: number;
  offset: number;
  status?: string;
}): Promise<ServiceRead<DeletionRequestPage>> {
  configureAdminApi();
  return settle(
    await deletionRequestsApiV1InternalDeletionRequestsGet({
      query: {
        limit: query.limit,
        offset: query.offset,
        ...(query.status ? { status: query.status } : {}),
      },
    }),
  );
}

/**
 * Clear the grace period and bring the member back. Explicit, operator-driven,
 * reason-bearing: nothing in this console restores an account on its own, and
 * a failure is never retried automatically. A second attempt answers 409
 * rather than silently succeeding, so a double submit cannot read as two acts.
 */
export async function restoreAccount(
  requestId: string,
  body: { reason: string },
): Promise<ServiceRead<DeletionRequest>> {
  configureAdminApi();
  return settle(
    await restoreDeletionRequestApiV1InternalDeletionRequestsRequestIdRestorePost({
      path: { request_id: requestId },
      body,
    }),
  );
}

/* ------------------------------------------------------------ restorability */

export type Restorability =
  | { restorable: true; expiresAt: Date }
  | { restorable: false; reason: 'completed' | 'already_restored' | 'expired' | 'unreadable' };

/**
 * Whether a row can still be restored, decided here rather than in a component
 * so the rule is one thing and is directly testable. A window that has passed
 * is not a disabled button: the hard delete has run and there is nothing left.
 */
export function restorability(request: DeletionRequest, now: Date = new Date()): Restorability {
  if (request.completed_at) return { restorable: false, reason: 'completed' };
  if (request.restored_at) return { restorable: false, reason: 'already_restored' };
  const expiresAt = new Date(request.hard_delete_after);
  if (Number.isNaN(expiresAt.getTime())) return { restorable: false, reason: 'unreadable' };
  if (expiresAt.getTime() <= now.getTime()) return { restorable: false, reason: 'expired' };
  return { restorable: true, expiresAt };
}

/** "3 days, 4 hours" - the grace window is the whole point, so it is spelled out. */
export function remainingGrace(expiresAt: Date, now: Date = new Date()): string {
  const ms = expiresAt.getTime() - now.getTime();
  if (ms <= 0) return 'expired';
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  if (days) return `${days} day${days === 1 ? '' : 's'}, ${hours} hour${hours === 1 ? '' : 's'}`;
  if (hours) return `${hours} hour${hours === 1 ? '' : 's'}, ${minutes % 60} minute(s)`;
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}
